import { ref, onMounted } from 'vue';
import axios from 'axios';
import apiClient from '@/services/axios';
import { useLoading } from '@/composables/useLoading';
import type { Device } from '@/types/device';
import { useI18n } from 'vue-i18n';

export const useDeviceManagement = ({ fetchOnMount = true }: { fetchOnMount?: boolean } = {}) => {
  const isLoadingFetch = ref(false);
  const errorMessage = ref('');
  const devices = ref<Device[]>([]);
  const filteredDevices = ref<Device[]>([]);

  const LAST_COUNT_KEY = 'devices:lastCount';
  const lastDeviceCount = ref(
    Number(localStorage.getItem(LAST_COUNT_KEY)) || 0
  );

  const currentPage = ref(1);
  const totalElements = ref(0);
  const totalPages = ref(0);
  const elementsPerPage = ref(20);
  const { t } = useI18n();
  const { showLoading, hideLoading } = useLoading();

  const handleError = (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.data) {
      errorMessage.value = error.response.data.message || t('errors.global');
    } else if (error instanceof Error) {
      errorMessage.value = error.message;
    } else {
      errorMessage.value = t('errors.global');
    }
  };

  const fetchDevices = async (page = 1, showLoader = true) => {
    if (showLoader) {
      showLoading();
      isLoadingFetch.value = true;
    }

    errorMessage.value = '';

    try {
      const { data } = await apiClient.get('/devices', {
        params: { page: String(page), per_page: String(elementsPerPage.value) },
        headers: { Accept: 'application/ld+json' },
      });

      if (data && data['hydra:member']) {
        devices.value = data['hydra:member'];
        filteredDevices.value = [...devices.value];
        totalElements.value = data['hydra:totalItems'];
        totalPages.value = Math.ceil(totalElements.value / elementsPerPage.value);
        lastDeviceCount.value = devices.value.length;
        localStorage.setItem(LAST_COUNT_KEY, String(lastDeviceCount.value));
      }
    } catch (error) {
      handleError(error);
    } finally {
      if (showLoader) {
        hideLoading();
        isLoadingFetch.value = false;
      }
    }
  };

  const changePage = (page: number) => {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page;
      fetchDevices(page);
    }
  };

  const changeElementsPerPage = (count: number) => {
    elementsPerPage.value = count;
    currentPage.value = 1;
    fetchDevices(currentPage.value);
  };

  const addDeviceToList = (newDevice: Device) => {
    devices.value.push(newDevice);
    filteredDevices.value.push(newDevice);
  };

  const toggleDescription = (device: Device) => {
    device.showDescription = !device.showDescription;
  };

  onMounted(() => {
    if (fetchOnMount) fetchDevices(currentPage.value, true);
  });


  return {
    isLoadingFetch,
    errorMessage,
    devices,
    filteredDevices,
    currentPage,
    totalPages,
    totalElements,
    elementsPerPage,
    lastDeviceCount,
    fetchDevices,
    changePage,
    changeElementsPerPage,
    addDeviceToList,
    toggleDescription,
  };
};
